import { FileText } from 'lucide-react'
import { InfoNotice } from '@/components/patterns/info-notice'
import { Card } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import type { FsnImportCustomPageWithContent } from '@/lib/services/fsn-imports/fsn-imports.types'

type FsnCustomPagesWithContentProps = {
  pages: FsnImportCustomPageWithContent[]
}

function countLabel(count: number, singular: string, plural: string): string {
  return `${count.toLocaleString('en-US')} ${count === 1 ? singular : plural}`
}

function describePageContent(page: FsnImportCustomPageWithContent): string {
  return [
    page.text_characters > 0 ? countLabel(page.text_characters, 'character', 'characters') : null,
    page.images > 0 ? countLabel(page.images, 'image', 'images') : null,
    page.embeds.length > 0 ? `Embeds: ${page.embeds.join(', ')}` : null,
  ]
    .filter((part) => part !== null)
    .join(' · ')
}

/**
 * The florist's FSN custom pages whose text, images and embeds the import leaves behind; support
 * can rebuild them as custom pages. Their products follow the import's product settings, so the
 * counts are what's on the FSN page, not what will import.
 */
export function FsnCustomPagesWithContent({ pages }: FsnCustomPagesWithContentProps) {
  if (pages.length === 0) {
    return null
  }

  return (
    <div className="space-y-3">
      <InfoNotice
        icon={<FileText />}
        title={`${countLabel(pages.length, 'custom page has', 'custom pages have')} text or images to rebuild`}
        body="Their text, images and embeds don't import; their products follow your product settings. Rebuild the pages as custom pages if the florist wants them."
      />
      <Card className="gap-0 overflow-hidden py-0">
        <div className="divide-y divide-border/70">
          {pages.map((page) => (
            <div
              key={page.page_id}
              className="grid gap-2 px-3 py-3 @3xl/fsn-preview:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_9rem] @3xl/fsn-preview:items-start"
            >
              <div className="min-w-0">
                <Typography variant="label" className="block break-words">
                  {page.title || 'Untitled page'}
                </Typography>
                <Typography variant="caption" tone="muted" className="mt-1 block break-words">
                  {page.path}
                </Typography>
              </div>
              <Typography variant="bodySmall" tone="muted" className="min-w-0">
                {describePageContent(page)}
              </Typography>
              <Typography
                variant="bodySmall"
                tone="muted"
                className="@3xl/fsn-preview:text-right @3xl/fsn-preview:whitespace-nowrap"
              >
                {page.products > 0
                  ? `${countLabel(page.products, 'product', 'products')} on FSN`
                  : 'No products'}
              </Typography>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
